<template>
    <kiosk-group-details :teamId="props.teamId" :groupId="props.groupId">
        <div class="flex flex-col w-full justify-center">
            <div class="max-w-(--breakpoint-sm) flex flex-col">
                <page-callout section="kiosk-group-billing-config" class="mb-4" />

                <page-header>Billing Config</page-header>

                <vx-expandable-card>
                    <template v-slot:header>Enable Billing</template>
                    <template v-slot:content>
                        <div class="flex flex-col gap-4 mt-3">
                            <p class="text-sm text-gray-700">
                                Enable billing identity for this group. This creates a Stripe customer and allows
                                billing contact information and payment method to be configured.
                            </p>

                            <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                <div class="flex gap-3">
                                    <div class="flex-shrink-0">
                                        <font-awesome-icon
                                            :icon="faExclamationTriangle"
                                            class="text-amber-600 text-lg"
                                        />
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <p class="font-semibold text-sm text-amber-900">
                                            Important: This action is permanent!
                                        </p>
                                        <ul class="list-disc list-inside text-sm text-amber-800 space-y-1">
                                            <li>Once enabled, billing cannot be disabled</li>
                                            <li>The group cannot be deleted after enabling billing</li>
                                            <li>Groups currently default to no billed usage</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div class="flex gap-3">
                                    <div class="flex-shrink-0">
                                        <font-awesome-icon :icon="faLightbulb" class="text-blue-600 text-lg" />
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <p class="font-semibold text-sm text-blue-900">What happens after enabling?</p>
                                        <ul class="list-disc list-inside text-sm text-blue-800 space-y-1">
                                            <li>A Stripe customer account will be created</li>
                                            <li>You can configure billing contact details and payment method</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <vx-button
                                v-if="team?.status !== 'closed'"
                                data-test="enable-billing-button"
                                @click="enableBilling"
                                color="primary"
                                size="lg"
                                :loading="enabling"
                                class="w-full"
                            >
                                Enable Billing
                            </vx-button>

                            <p v-if="team?.status === 'closed'" class="text-sm text-gray-500 text-center">
                                Billing cannot be enabled for groups on closed teams.
                            </p>
                        </div>
                    </template>
                </vx-expandable-card>
            </div>
        </div>
    </kiosk-group-details>
</template>

<script setup>
import { ref } from 'vue';
import KioskGroupDetails from '~/components/kiosk/kiosk-groups/details/KioskGroupDetails.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import { VxButton, VxExpandableCard } from '@voxie/frontend-components';
import { useGroupDetails } from '~/components/kiosk/kiosk-groups/composables/useGroupDetails';
import adminGroupsClient from '~/services/adminGroupsClient';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { faExclamationTriangle, faLightbulb } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
    teamId: { type: Number, required: true },
    groupId: { type: String, required: true },
});

const { team, refreshGroup } = useGroupDetails();
const toasted = useToasted();
const enabling = ref(false);

const enableBilling = async () => {
    if (enabling.value) {
        return;
    }

    enabling.value = true;

    try {
        await adminGroupsClient.enableBilling(props.teamId, props.groupId);
        await refreshGroup(props.teamId, props.groupId);
        toasted.success('Billing has been enabled for this group');
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        enabling.value = false;
    }
};
</script>
